import { Link } from "@tanstack/react-router"
import { ArrowUpRight, ChartColumn, FileInput, Network, NotebookTabs, Shapes, ShieldCheck, TableProperties } from "lucide-react"
import { useMemo } from "react"
import { ErrorState } from "@/components/states"
import { Skeleton } from "@/components/ui/skeleton"
import { useFormatNumber, useLocale, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { useOntology } from "@/workspace/hooks"
import { metricTiles } from "@/workspace/metrics"
import { DataFrame, Stat } from "./frame"

export function DataHomePage() {
  const t = useT()
  const scope = useScope()
  const formatNumber = useFormatNumber()
  const { preference } = useLocale()
  const { ontology, defs } = useOntology(scope)
  const summary = useMemo(() => {
    if (!ontology || !defs.data) return null
    const types = [...ontology.types.values()]
    const kinds = (kind: string) => defs.data.artifacts.filter((a) => a.kind === kind).length
    return {
      tenant: types.filter((t) => t.layer === "tenant").length,
      core: types.filter((t) => t.layer === "registry").length,
      properties: types.reduce((n, t) => n + t.fields.length, 0),
      links: types.reduce((n, t) => n + t.links.length, 0),
      bridges: ontology.relations.reduce((n, r) => n + r.links.length, 0),
      metrics: metricTiles(defs.data, ontology, preference.locale).length,
      sources: kinds("source_schema"),
      mappings: kinds("mapping"),
      policies: kinds("policy"),
      version: defs.data.version,
    }
  }, [ontology, defs.data, preference.locale])

  const lenses = [
    { to: "/$orgId/$environmentId/data/workbooks", icon: <NotebookTabs />, title: t("wb.nav"), body: t("dv.home.workbooks") },
    { to: "/$orgId/$environmentId/data/ontology", icon: <Shapes />, title: t("dv.nav.ontology"), body: t("dv.home.ontology") },
    { to: "/$orgId/$environmentId/data/explore", icon: <TableProperties />, title: t("dv.nav.explore"), body: t("dv.home.explore") },
    { to: "/$orgId/$environmentId/graph", icon: <Network />, title: t("nav.graph"), body: t("dv.home.graph") },
    { to: "/$orgId/$environmentId/data/metrics", icon: <ChartColumn />, title: t("dv.nav.metrics"), body: t("dv.home.metrics") },
    { to: "/$orgId/$environmentId/data/sources", icon: <FileInput />, title: t("dv.nav.sources"), body: t("dv.home.sources") },
    { to: "/$orgId/$environmentId/data/policy", icon: <ShieldCheck />, title: t("dv.nav.policy"), body: t("dv.home.policy") },
  ] as const

  return (
    <DataFrame title={t("dv.home.title")} description={t("dv.home.description")} how={["dv.how.home"]}>
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : summary ? (
        <div className="grid grid-cols-2 gap-x-8 gap-y-5 border-y border-border py-5 sm:grid-cols-4 lg:grid-cols-7">
          <Stat label={t("dv.home.types")} value={formatNumber(summary.tenant + summary.core)} hint={t("dv.home.typesHint", { tenant: formatNumber(summary.tenant), core: formatNumber(summary.core) })} />
          <Stat label={t("dv.home.properties")} value={formatNumber(summary.properties)} />
          <Stat label={t("dv.home.links")} value={formatNumber(summary.links)} hint={t("dv.home.bridges", { count: summary.bridges })} />
          <Stat label={t("dv.nav.metrics")} value={formatNumber(summary.metrics)} />
          <Stat label={t("dv.home.sourceSystems")} value={formatNumber(summary.sources)} hint={t("dv.home.mappings", { count: summary.mappings })} />
          <Stat label={t("dv.home.policies")} value={formatNumber(summary.policies)} />
          <Stat label={t("dv.home.version")} value={<span className="font-mono text-lg">v{summary.version}</span>} />
        </div>
      ) : (
        <Skeleton className="h-20 w-full rounded-2xl" />
      )}
      <ul className="grid gap-x-10 sm:grid-cols-2">
        {lenses.map((lens) => (
          <li key={lens.to}>
            <Link
              to={lens.to}
              params={scope}
              search={{}}
              className="group flex items-start gap-4 border-b border-border py-5 outline-none focus-visible:bg-muted/40 [&>svg]:mt-0.5 [&>svg]:size-5 [&>svg]:shrink-0 [&>svg]:text-muted-foreground"
            >
              {lens.icon}
              <span className="min-w-0 flex-1 space-y-1">
                <span className="flex items-center gap-1.5 text-[15px] font-semibold tracking-tight">
                  {lens.title}
                  <ArrowUpRight className="size-3.5 text-subtle-foreground opacity-0 transition-opacity group-hover:opacity-100 rtl:-scale-x-100" aria-hidden />
                </span>
                <span className="block text-sm text-muted-foreground">{lens.body}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </DataFrame>
  )
}
